import { useSelector } from "react-redux";
import { fetchAddress, formatPayment, formatScheduledDate } from "../../../../../lib/utils";
import { Card, CardHeader, CardContent, CardTitle } from "../../../../ui/card";
import { Clock, DollarSign, Calendar, MapPin, Navigation } from "lucide-react";
import { useState, useEffect } from "react";

export function JobInformation({ jobData }) {
  const [address, setAddress] = useState("");
  const [loadingAddress, setLoadingAddress] = useState(true);
  const [error, setError] = useState(null);
  const userInfo = useSelector(state => state.auth.userInfo);
  const isShoveller = userInfo?.user?.role === 'shoveller';
  const formatScheduledTime = () => {
    const { hour, minute, period } = jobData.scheduledTime;
    return `${hour}:${minute} ${period}`;
  };

  const calculateArrivalWindowText = () => {
  const arrivalWindow = jobData?.window || '2h';
  const { hour, minute, period } = jobData.scheduledTime;
  
  // Parse the window value (could be '2h', '3h', '4h', etc.)
  const windowHours = parseInt(arrivalWindow.replace('h', ''));
  
  // Convert start time to 24-hour format for easier calculation
  let startHour24 = parseInt(hour);
  
  // Adjust for AM/PM
  if (period === 'PM' && startHour24 !== 12) {
    startHour24 += 12;
  } else if (period === 'AM' && startHour24 === 12) {
    startHour24 = 0; // Midnight
  }
  
  // Calculate end time
  const endHour24 = (startHour24 + windowHours) % 24;
  
  // Format end time back to 12-hour format
  let endHour12 = endHour24 % 12 || 12;
  const endPeriod = endHour24 >= 12 ? 'PM' : 'AM';
  
  // Format start time
  const startHour12 = startHour24 % 12 || 12;
  const startPeriod = startHour24 >= 12 ? 'PM' : 'AM';
  
  // Ensure two-digit minutes
  const formattedMinute = minute.padStart(2, '0');
  const endMinute = minute.padStart(2, '0'); // Same minutes as start
  
  // Pluralize "hour(s)"
  const hourText = windowHours === 1 ? 'hour' : 'hours';
  
  return `${windowHours} ${hourText} from ${startHour12}:${formattedMinute} ${startPeriod} to ${endHour12}:${endMinute} ${endPeriod}`;
};

  useEffect(() => {
    const fetchAddressData = async () => {
      if (!jobData?.location?.coordinates || jobData.location.coordinates.length !== 2) {
        setError("Invalid location data");
        setLoadingAddress(false);
        return;
      }

      try {
        setLoadingAddress(true);
        setError(null);
        
        // Extract coordinates - [0] is longitude, [1] is latitude
        const [longitude, latitude] = jobData.location.coordinates;
        const addressString = await fetchAddress(latitude, longitude);
        
        setAddress(addressString);
      } catch (err) {
        setError("Failed to fetch address");
        console.error("Error fetching address:", err);
      } finally {
        setLoadingAddress(false);
      }
    };

    fetchAddressData();
  }, [jobData?.location?.coordinates]);

  console.log(jobData);

  const arrivalWindow = jobData?.window || '2h';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="w-5 h-5" />
          Service Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Address Card - Always spans full width */}
        <div className="col-span-full">
          <div className="flex items-center gap-3 p-4 bg-gray-50">
            <Navigation className="w-5 h-5" />
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-1">Service Address</p>
              {loadingAddress ? (
                <div className="space-y-2">
                  <div className="h-4 bg-gray-300 rounded animate-pulse w-3/4"></div>
                </div>
              ) : error ? (
                <p className="text-red-600 font-medium">{error}</p>
              ) : (
                <p className="font-semibold text-gray-900">{address}</p>
              )}
            </div>
          </div>
        </div>

        {/* Other Info Items */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InfoItem 
            icon={<Calendar className="w-5 h-5 text-blue-600" />}
            label="Service Date"
            value={formatScheduledDate(jobData.scheduledDate)}
          />
          <InfoItem 
            icon={<Clock className="w-5 h-5 text-purple-600" />}
            label="Service Time"
            value={formatScheduledTime()}
          />
          <InfoItem 
            icon={<Clock className="w-5 h-5 text-purple-600" />}
            label="Arrival Window"
            value={calculateArrivalWindowText()}
          />
          <InfoItem 
            icon={<DollarSign className="w-5 h-5 text-green-600" />}
            label="Amount"
            value={`${formatPayment(jobData.paymentInfo.amount, { is80: isShoveller, isVolunteer: jobData.isRequestedForVolunteer, isShoveller })}`}
          />
          <InfoItem 
            icon={<Clock className="w-5 h-5 text-orange-600" />}
            label="Service Duration"
            value={jobData.completionTime}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function InfoItem({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
      {icon}
      <div>
        <p className="text-sm text-gray-600">{label}</p>
        <p className="font-semibold">{value}</p>
      </div>
    </div>
  );
}